import React, { useRef, useState } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Upload, CheckCircle2, AlertTriangle, FileSpreadsheet } from "lucide-react";
import { importBestand, templateDownloadUrl } from "@/lib/api";
import { toast } from "sonner";

/**
 * Excel-basierter Bestands-Import pro Entität.
 * Workflow:
 *   1. Template herunterladen
 *   2. Datei ausfüllen und auswählen
 *   3. "Prüfen" (dry_run) → zeigt Anzahl & Fehler
 *   4. "Importieren" → legt tatsächlich an
 */
const BestandImportDialog = ({ open, onOpenChange, entity, onImported }) => {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);   // dry_run result
    const [result, setResult] = useState(null);     // commit result
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);

    const reset = () => {
        setFile(null); setPreview(null); setResult(null);
        if (fileRef.current) fileRef.current.value = "";
    };
    const close = () => { reset(); onOpenChange(false); };

    const handleFile = (e) => {
        const f = e.target.files?.[0];
        setFile(f || null);
        setPreview(null); setResult(null);
    };

    const runCheck = async () => {
        if (!file) { toast.error("Bitte Excel-Datei auswählen."); return; }
        setBusy(true); setResult(null);
        try {
            const r = await importBestand(entity.id, file, true);
            setPreview(r);
            if (r.errors?.length) {
                toast.warning(`${r.errors.length} Fehler gefunden – bitte beheben.`);
            } else {
                toast.success(`Bereit zum Import: ${r.counts_planned.properties} Immobilien · ${r.counts_planned.units} Einheiten · ${r.counts_planned.tenants} Mieter`);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Prüfung fehlgeschlagen.");
        } finally { setBusy(false); }
    };

    const runImport = async () => {
        if (!file) return;
        setBusy(true);
        try {
            const r = await importBestand(entity.id, file, false);
            setResult(r);
            if (r.errors?.length) {
                toast.error(`Import abgebrochen – ${r.errors.length} Fehler.`);
            } else {
                toast.success(`Importiert: ${r.created.properties} Immobilien, ${r.created.units} Einheiten, ${r.created.tenants} Mieter`);
                onImported?.();
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Import fehlgeschlagen.");
        } finally { setBusy(false); }
    };

    const errors = preview?.errors || result?.errors || [];
    const warnings = preview?.warnings || result?.warnings || [];
    const formatNote = preview?.format_note || result?.format_note;
    const counts = result?.created || preview?.counts_planned;

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Bestand importieren · {entity?.name}</DialogTitle>
                </DialogHeader>

                <div className="space-y-5 py-2">
                    {/* Step 1: Template */}
                    <div className="rounded-md border border-gray-200 p-4 bg-gray-50">
                        <div className="flex items-start gap-3">
                            <FileSpreadsheet size={18} className="text-slate-700 mt-0.5" />
                            <div className="flex-1">
                                <div className="text-sm font-semibold text-slate-900">1. Vorlage herunterladen</div>
                                <div className="text-xs text-gray-600 mt-1">
                                    Excel-Datei mit 3 Tabellenblättern: <em>Wirtschaftseinheiten</em>, <em>Mieteinheiten</em>, <em>Mieter</em>.
                                    Beispielzeilen zeigen das erwartete Format – einfach überschreiben oder ergänzen.
                                </div>
                                <a href={templateDownloadUrl} className="inline-block mt-3">
                                    <Button variant="outline" size="sm" data-testid="btn-download-template">
                                        <Download size={14} className="mr-1.5" /> Template.xlsx
                                    </Button>
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* Step 2: Upload */}
                    <div className="rounded-md border border-gray-200 p-4">
                        <div className="text-sm font-semibold text-slate-900">2. Ausgefüllte Datei auswählen</div>
                        <input
                            ref={fileRef}
                            type="file"
                            accept=".xlsx,.xlsm"
                            onChange={handleFile}
                            className="mt-3 block text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-gray-300 file:bg-white file:text-sm file:font-medium hover:file:bg-gray-50"
                            data-testid="input-import-file"
                        />
                        {file && (
                            <div className="mt-2 text-xs text-gray-600">{file.name} · {(file.size / 1024).toFixed(1)} KB</div>
                        )}
                    </div>

                    {/* Step 3: Check + Import */}
                    <div className="rounded-md border border-gray-200 p-4">
                        <div className="text-sm font-semibold text-slate-900">3. Prüfen und importieren</div>
                        <div className="text-xs text-gray-600 mt-1">
                            Erst „Prüfen" klicken – Fehler werden mit Zeilennummer gemeldet, ohne dass Daten geändert werden.
                        </div>
                        <div className="flex items-center gap-2 mt-3">
                            <Button variant="outline" size="sm" onClick={runCheck} disabled={!file || busy} data-testid="btn-check-import">
                                Prüfen
                            </Button>
                            <Button
                                size="sm"
                                onClick={runImport}
                                disabled={!file || busy || !preview || errors.length > 0 || !!result?.created?.properties}
                                className="bg-slate-900 hover:bg-slate-800"
                                data-testid="btn-commit-import"
                            >
                                <Upload size={14} className="mr-1.5" /> Importieren
                            </Button>
                        </div>
                    </div>

                    {/* Result summary */}
                    {counts && (
                        <div className={`rounded-md border p-4 ${errors.length > 0 ? "border-red-200 bg-red-50" : result ? "border-emerald-200 bg-emerald-50" : "border-sky-200 bg-sky-50"}`}>
                            <div className="flex items-start gap-2">
                                {errors.length > 0
                                    ? <AlertTriangle size={16} className="text-red-600 mt-0.5" />
                                    : <CheckCircle2 size={16} className="text-emerald-600 mt-0.5" />}
                                <div className="flex-1 text-sm">
                                    <div className="font-semibold text-slate-900">
                                        {result ? "Import abgeschlossen" : errors.length > 0 ? "Fehler in der Datei" : "Bereit zum Import"}
                                    </div>
                                    {formatNote && (
                                        <div className="text-[11px] text-slate-700 italic mt-0.5">{formatNote}</div>
                                    )}
                                    <div className="mt-1 text-xs text-slate-700 tabular-nums">
                                        Wirtschaftseinheiten: <strong>{counts.properties}</strong> ·
                                        {" "}Mieteinheiten: <strong>{counts.units}</strong> ·
                                        {" "}Mieter: <strong>{counts.tenants}</strong>
                                    </div>
                                </div>
                            </div>
                            {warnings.length > 0 && (
                                <div className="mt-3 space-y-1">
                                    <div className="text-[10px] uppercase tracking-wider text-amber-700 font-medium">Hinweise</div>
                                    <div className="max-h-32 overflow-y-auto space-y-1">
                                        {warnings.map((w, i) => (
                                            <div key={i} className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                                                {w}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {errors.length > 0 && (
                                <div className="mt-3 max-h-48 overflow-y-auto space-y-1">
                                    {errors.map((e, i) => (
                                        <div key={i} className="text-xs text-red-700 font-mono bg-white border border-red-100 rounded px-2 py-1">
                                            [{e.sheet} · Zeile {e.row}] {e.msg}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={close} disabled={busy}>Schließen</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default BestandImportDialog;
